import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Barcode, ChevronLeft, ChevronRight, Info, List, ScanBarcode, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import AuthMessage from '../../components/AuthMessage';
import { useAuth } from '../../context/AuthContext';
import { useProducts } from '../../context/ProductsContext';
import { listProducts, lookupByBarcode } from '../../api/inventory';
import { formatCurrency } from '../../data/mockData';
import { formatPrice, formatStock } from '../../utils/soldBy';
import {
  SOLD_BY_MISMATCH_COPY,
  collectBarcodeAliases,
  isMergedProduct,
  soldByMismatch,
} from '../../utils/productMerge';
import ProductMerge from './ProductMerge';
import styles from './Catalog.module.css';
import pageStyles from './ProductMergePage.module.css';

const PAGE_SIZE = 10;

function looksLikeBarcode(value) {
  const code = String(value || '').trim();
  return /^\d{6,18}$/.test(code);
}

function matchesText(product, needle) {
  const haystack = `${product.name || ''} ${product.sku || ''} ${product.barcode || ''}`.toLowerCase();
  return haystack.includes(needle);
}

function hasCurrentBarcode(product, code) {
  return String(product?.barcode || '').trim() === String(code || '').trim();
}

function statusLabel(status) {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'INACTIVE') return 'Inactive';
  return 'Draft';
}

function ProductTable({
  rows,
  action,
  selectedIds,
  staysId,
  categoryById,
  onAdd,
  onRemove,
  rangeStart,
  rangeEnd,
  total,
  page,
  pages,
  onPage,
}) {
  const categoryName = (product) => categoryById.get(product.category)?.name || '—';

  const actionCell = (product) => {
    if (action === 'remove') {
      return (
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => onRemove(product.id)}
          aria-label={`Remove ${product.name}`}
        >
          <X size={14} />
        </button>
      );
    }
    const added = selectedIds.includes(product.id);
    return (
      <Button
        type="button"
        size="sm"
        variant={added ? 'ghost' : 'outline'}
        disabled={added}
        onClick={() => onAdd(product)}
      >
        {added ? 'Added' : 'Add'}
      </Button>
    );
  };

  return (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Barcode</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Status</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((product) => {
              const low = product.stock <= product.lowStockThreshold;
              return (
                <tr key={product.id}>
                  <td>
                    <div className={styles.productCell}>
                      <span className={styles.swatch} style={{ background: `${product.swatch}22`, color: product.swatch }}>
                        {product.images?.[0]?.url
                          ? <img src={product.images[0].url} alt="" className={styles.swatchImg} />
                          : product.emoji}
                      </span>
                      <div className={styles.productMeta}>
                        <p className={styles.productName}>
                          {product.name}
                          {product.id === staysId && <span className={styles.needsDetailsBadge}>Stays</span>}
                        </p>
                        <p className={styles.productSku}>{product.sku}</p>
                      </div>
                    </div>
                  </td>
                  <td><span className={styles.categoryTag}>{categoryName(product)}</span></td>
                  <td>
                    <span className={styles.barcode}><Barcode size={12} /> {product.barcode || '—'}</span>
                  </td>
                  <td>
                    <span className={styles.priceCell}>
                      <span className={styles.price}>{formatPrice(product, formatCurrency)}</span>
                      {product.mrp > product.price && <span className={styles.mrp}>{formatCurrency(product.mrp)}</span>}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.stockPill} ${low ? styles.stockLow : ''}`}>
                      {low && <AlertTriangle size={11} />}
                      {formatStock(product.stock, product)}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.statusChip} ${product.status === 'ACTIVE' ? styles.active : styles.draft}`}>
                      {statusLabel(product.status)}
                    </span>
                  </td>
                  <td>
                    <div className={styles.rowActions}>{actionCell(product)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {page != null && (
          <div className={styles.paginationBar}>
            <span className={styles.paginationSummary}>
              Showing {rangeStart}–{rangeEnd} of {total} product{total === 1 ? '' : 's'}
            </span>
            <div className={styles.paginationControls}>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => onPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              <span className={styles.paginationPage}>Page {page} of {pages}</span>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => onPage(Math.min(pages, page + 1))}
                disabled={page >= pages}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.mobileList} aria-label="Products">
        {rows.map((product) => {
          const low = product.stock <= product.lowStockThreshold;
          const added = selectedIds.includes(product.id);
          return (
            <article key={product.id} className={styles.mobileCard}>
              <div className={styles.mobileCardTop}>
                <span className={styles.mobileThumb} style={{ background: `${product.swatch}22`, color: product.swatch }}>
                  {product.images?.[0]?.url
                    ? <img src={product.images[0].url} alt="" className={styles.swatchImg} />
                    : product.emoji}
                </span>
                <div className={styles.mobileBody}>
                  <div className={styles.mobileTop}>
                    <h3 className={styles.mobileName}>{product.name}</h3>
                    <span className={`${styles.statusDot} ${product.status === 'ACTIVE' ? styles.statusDotActive : ''}`} title={statusLabel(product.status)} />
                  </div>
                  <p className={styles.mobileMeta}>
                    {categoryName(product)}
                    {product.sku ? ` · ${product.sku}` : ''}
                    {product.id === staysId ? ' · Stays' : ''}
                  </p>
                </div>
              </div>
              <div className={styles.mobileCardFooter}>
                <div className={styles.mobileStats}>
                  <span className={styles.mobilePrice}>
                    {formatPrice(product, formatCurrency)}
                    {product.mrp > product.price && <span className={styles.mrpInline}>{formatCurrency(product.mrp)}</span>}
                  </span>
                  <span className={`${styles.stockPill} ${low ? styles.stockLow : ''}`}>
                    {low && <AlertTriangle size={11} />}
                    {formatStock(product.stock, product)}
                  </span>
                </div>
                <div className={styles.mobileActions}>
                  {action === 'remove' ? (
                    <button type="button" className={styles.mobileActionBtn} onClick={() => onRemove(product.id)}>
                      <X size={15} />
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={styles.mobileActionBtn}
                      disabled={added}
                      onClick={() => onAdd(product)}
                    >
                      {added ? 'Added' : 'Add'}
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
        {page != null && (
          <div className={styles.mobilePagination}>
            <span className={styles.paginationSummary}>{rangeStart}–{rangeEnd} of {total}</span>
            <div className={styles.paginationControls}>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => onPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              <span className={styles.paginationPage}>{page} / {pages}</span>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => onPage(Math.min(pages, page + 1))}
                disabled={page >= pages}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function ProductMergePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    products, productsLoading, mergeProducts, forgetProducts, categories,
  } = useProducts();
  const [query, setQuery] = useState('');
  const [remoteMatches, setRemoteMatches] = useState([]);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionNote, setSelectionNote] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeHidden, setMergeHidden] = useState(false);
  const [showList, setShowList] = useState(false);
  const [listPage, setListPage] = useState(1);

  const canMerge = Boolean(user?.groups?.includes('Admin') || user?.groups?.includes('Ops'));

  const categoryById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => map.set(category.id, category));
    return map;
  }, [categories]);

  const catalogProducts = useMemo(
    () => products.filter((product) => !isMergedProduct(product)),
    [products],
  );
  const listPages = Math.max(1, Math.ceil(catalogProducts.length / PAGE_SIZE));
  const currentListPage = Math.min(listPage, listPages);
  const listed = catalogProducts.slice((currentListPage - 1) * PAGE_SIZE, currentListPage * PAGE_SIZE);

  const selectedProducts = selectedIds
    .map((id) => products.find((product) => product.id === id))
    .filter((product) => product && !isMergedProduct(product));
  const mergeMismatch = soldByMismatch(selectedProducts);
  const mergeEnabled = canMerge
    && !mergeHidden
    && selectedProducts.length >= 2
    && selectedProducts.length <= 6
    && !mergeMismatch;

  const addToMerge = (product) => {
    if (!product?.id || isMergedProduct(product)) return;
    setSelectedIds((prev) => {
      if (prev.includes(product.id)) {
        setSelectionNote('That product is already added.');
        return prev;
      }
      if (prev.length >= 6) {
        setSelectionNote('Add 2 to 6 products.');
        return prev;
      }
      setSelectionNote('');
      return [...prev, product.id];
    });
  };

  const removeFromMerge = (id) => {
    setSelectionNote('');
    setSelectedIds((prev) => prev.filter((itemId) => itemId !== id));
  };

  const matches = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    const needle = q.toLowerCase();
    const seen = new Set();
    const combined = [];
    [...remoteMatches, ...products].forEach((product) => {
      if (!product?.id || seen.has(product.id) || isMergedProduct(product)) return;
      if (!matchesText(product, needle)) return;
      seen.add(product.id);
      combined.push(product);
    });
    return combined;
  }, [query, products, remoteMatches]);

  useEffect(() => {
    const q = query.trim();
    if (!user?.idToken || q.length < 2) {
      setRemoteMatches([]);
      setSearchedQuery('');
      setSearchError('');
      setBusy(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setBusy(true);
      setSearchError('');
      try {
        if (looksLikeBarcode(q)) {
          try {
            const data = await lookupByBarcode(q, user.idToken);
            const found = data?.item;
            if (cancelled) return;
            if (found && hasCurrentBarcode(found, q) && !isMergedProduct(found)) {
              mergeProducts([found]);
              setRemoteMatches([found]);
            } else {
              setRemoteMatches([]);
            }
          } catch (err) {
            if (cancelled) return;
            setRemoteMatches([]);
            const notFound = err?.status === 404 || err?.message === 'not_found' || err?.code === 'not_found';
            if (!notFound) throw err;
          }
        } else {
          const { items } = await listProducts(user.idToken, { q, limit: 20, includeInactive: true });
          if (cancelled) return;
          const remote = (items || []).filter((product) => !isMergedProduct(product));
          if (remote.length) mergeProducts(remote);
          setRemoteMatches(remote);
        }
        setSearchedQuery(q);
      } catch {
        if (!cancelled) {
          setRemoteMatches([]);
          setSearchedQuery(q);
          setSearchError('Could not search the catalog.');
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, user?.idToken, mergeProducts]);

  const addExactBarcode = async (raw) => {
    const q = String(raw || '').trim();
    if (!looksLikeBarcode(q) || !user?.idToken) return;
    let item = products.find((product) => !isMergedProduct(product) && hasCurrentBarcode(product, q));
    if (!item) {
      try {
        const data = await lookupByBarcode(q, user.idToken);
        const found = data?.item;
        if (found && hasCurrentBarcode(found, q) && !isMergedProduct(found)) {
          item = found;
          mergeProducts([found]);
        }
      } catch {
        item = null;
      }
    }
    if (!item) {
      setSearchError('No product found for that barcode.');
      return;
    }
    addToMerge(item);
    setQuery('');
    setRemoteMatches([]);
    setSearchedQuery('');
  };

  const handleMerged = ({ survivor, donorIds, aliases }) => {
    forgetProducts(donorIds);
    if (survivor?.id) {
      mergeProducts([{
        ...survivor,
        barcodeAliases: collectBarcodeAliases(survivor.barcode, aliases),
      }]);
    }
    setSelectedIds([]);
    setRemoteMatches([]);
    setSearchedQuery('');
    setSelectionNote('');
    setMergeOpen(false);
    if (survivor?.id) navigate(`/products?edit=${encodeURIComponent(survivor.id)}`);
  };

  const listRangeStart = catalogProducts.length === 0 ? 0 : ((currentListPage - 1) * PAGE_SIZE) + 1;
  const listRangeEnd = Math.min(currentListPage * PAGE_SIZE, catalogProducts.length);
  const staysId = selectedProducts[0]?.id;

  if (!canMerge || mergeHidden) {
    return (
      <div>
        <PageHeader eyebrow="Catalog" title="Merge products" />
        <AuthMessage>You do not have permission to merge products.</AuthMessage>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Catalog"
        title="Merge products"
        subtitle="Combine duplicate products into one."
        actions={(
          <Button disabled={!mergeEnabled} onClick={() => setMergeOpen(true)}>
            {selectedProducts.length >= 2 ? `Merge ${selectedProducts.length} products` : 'Merge'}
          </Button>
        )}
      />

      <div className={pageStyles.note}>
        <Info size={15} />
        <span>Type a name, SKU, or barcode and the matches appear as you type. Or choose Select from list. Add 2 to 6 products sold the same way, then Merge. The first one you add stays.</span>
      </div>

      <div className={styles.toolbar}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            addExactBarcode(query);
          }}
        >
          <div className={`${styles.searchBox} ${busy ? styles.searchBoxBusy : ''}`}>
            <ScanBarcode size={16} className={styles.searchScanIcon} aria-hidden />
            <input
              placeholder="Scan barcode, or search by name / SKU…"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setSearchError('');
              }}
              autoComplete="off"
              inputMode="search"
              aria-label="Find a product to merge"
            />
            {query && (
              <button
                type="button"
                className={styles.clearSearch}
                onClick={() => {
                setQuery('');
                setRemoteMatches([]);
                setSearchedQuery('');
                setSearchError('');
                }}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </form>
        <button
          type="button"
          className={`${styles.chip} ${showList ? styles.chipActive : ''}`}
          onClick={() => setShowList((open) => !open)}
          aria-pressed={showList}
        >
          <List size={14} />
          Select from list
        </button>
      </div>

      <AuthMessage>
        {searchError || (
          !busy && searchedQuery === query.trim() && query.trim().length >= 2 && matches.length === 0
            ? (looksLikeBarcode(query) ? 'No product found for that barcode.' : 'No product found for that name or SKU.')
            : ''
        )}
      </AuthMessage>
      {mergeMismatch && <AuthMessage>{SOLD_BY_MISMATCH_COPY}</AuthMessage>}
      {!mergeMismatch && selectionNote && <AuthMessage>{selectionNote}</AuthMessage>}

      {matches.length > 0 && (
        <section className={pageStyles.section}>
          <p className={pageStyles.sectionLabel}>Search results</p>
          <ProductTable
            rows={matches}
            action="add"
            selectedIds={selectedIds}
            staysId={staysId}
            categoryById={categoryById}
            onAdd={addToMerge}
          />
        </section>
      )}

      {showList && (
        <section className={pageStyles.section}>
          <p className={pageStyles.sectionLabel}>Catalog</p>
          {productsLoading ? (
            <p className={styles.categoryTag}>Loading products…</p>
          ) : catalogProducts.length === 0 ? (
            <EmptyState icon={List} title="No products" description="The catalog is empty." />
          ) : (
            <ProductTable
              rows={listed}
              action="add"
              selectedIds={selectedIds}
              staysId={staysId}
              categoryById={categoryById}
              onAdd={addToMerge}
              rangeStart={listRangeStart}
              rangeEnd={listRangeEnd}
              total={catalogProducts.length}
              page={currentListPage}
              pages={listPages}
              onPage={setListPage}
            />
          )}
        </section>
      )}

      <section className={pageStyles.section}>
        <p className={pageStyles.sectionLabel}>
          Products to merge
          {selectedProducts.length > 0 ? ` · ${selectedProducts.length} of 6` : ''}
        </p>
        {selectedProducts.length === 0 ? (
          <EmptyState
            icon={ScanBarcode}
            title="No products added yet"
            description="Scan a barcode, search by name or SKU, or select from the list."
          />
        ) : (
          <>
            <ProductTable
              rows={selectedProducts}
              action="remove"
              selectedIds={selectedIds}
              staysId={staysId}
              categoryById={categoryById}
              onRemove={removeFromMerge}
            />
            <p className={pageStyles.sectionHint}>
              {selectedProducts.length < 2
                ? 'Add one more product. The one marked Stays is the product you keep.'
                : 'The one marked Stays is the product you keep. You can change that on the next screen.'}
            </p>
          </>
        )}
      </section>

      <ProductMerge
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        selectedIds={selectedProducts.map((product) => product.id)}
        token={user?.idToken}
        categories={categories}
        onMerged={handleMerged}
        onForbidden={() => setMergeHidden(true)}
        onRefresh={() => {}}
        onOpenSurvivor={(itemId) => {
          setMergeOpen(false);
          navigate(`/products?edit=${encodeURIComponent(itemId)}`);
        }}
      />
    </div>
  );
}
